'use client'

import {
    Supplier,
    getSupplier,
    getSuppliers,
} from '@/lib/api/suppliers/supplier-api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MdBusiness, MdClose, MdSearch } from 'react-icons/md'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'

interface Props {
    value: number | null
    onChange: (supplierId: number | null, supplier: Supplier | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    /** `sm` para uso dentro de linha de tabela, onde o campo padrão não cabe. */
    size?: 'sm' | 'md'
}

export function SupplierSelector({
    value,
    onChange,
    label = 'Fornecedor',
    placeholder = 'Buscar fornecedor...',
    disabled = false,
    className = '',
    size = 'md',
}: Props) {
    const sm = size === 'sm'
    const [suppliers, setSuppliers] = useState<Supplier[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selected, setSelected] = useState<Supplier | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const load = async () => {
        setLoading(true)
        try {
            const res = await getSuppliers()
            if (res.success && Array.isArray(res.result))
                setSuppliers(res.result)
        } finally {
            setLoading(false)
        }
    }

    /**
     * Resolve o fornecedor do `value` pelo próprio id.
     *
     * A lista só era carregada quando o campo recebia foco, então em modo de EDIÇÃO —
     * onde o valor chega pronto e ninguém clica no campo — o componente nunca tinha a
     * lista e caía num rótulo inventado ("Fornecedor #124") no lugar do nome. Buscar o
     * registro pelo id resolve mesmo quando o fornecedor não está na listagem (inativo,
     * paginado, de outra unidade).
     */
    useEffect(() => {
        if (!value) {
            setSelected(null)
            return
        }
        if (selected?.id === value) return

        const found = suppliers.find(s => s.id === value)
        if (found) {
            setSelected(found)
            return
        }

        let cancelled = false
        getSupplier(value).then(res => {
            if (cancelled || !res.success || !res.result) return
            setSelected(res.result)
        })
        return () => {
            cancelled = true
        }
    }, [value, suppliers, selected])

    // O fechar-ao-clicar-fora mora no hook: com o painel em portal, ele não está mais
    // dentro do container e o handler antigo o trataria como "clique fora", fechando
    // antes de a opção ser escolhida.
    const closeDropdown = useCallback(() => setShowDropdown(false), [])
    const { anchorRef, renderPanel } = useAnchoredDropdown(
        showDropdown,
        closeDropdown
    )

    const filtered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase()
        if (!q) return suppliers
        return suppliers.filter(
            (s: any) =>
                s.name?.toLowerCase().includes(q) ||
                s.tradingName?.toLowerCase().includes(q) ||
                s.cnpj?.includes(q)
        )
    }, [suppliers, searchTerm])

    const handleClear = () => {
        setSelected(null)
        onChange(null, null)
        setSearchTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span
                        className={`label-text ${sm ? 'text-xs' : ''} font-medium`}
                    >
                        {label}
                    </span>
                </label>
            )}

            {selected ? (
                <div
                    className={`flex items-center gap-2 input input-bordered w-full ${sm ? 'input-sm' : ''}`}
                >
                    <MdBusiness
                        className={`text-base-content/60 ${sm ? 'text-sm' : ''}`}
                    />
                    <div className="flex-1 min-w-0">
                        <p
                            className={`font-medium truncate ${sm ? 'text-xs' : 'text-sm'}`}
                        >
                            {selected.name}
                        </p>
                        {!sm && (selected as any).cnpj && (
                            <p className="text-xs text-base-content/60 truncate">
                                CNPJ {(selected as any).cnpj}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={handleClear}
                        disabled={disabled}
                    >
                        <MdClose />
                    </button>
                </div>
            ) : (
                <div className="relative" ref={anchorRef}>
                    <MdSearch
                        className={`absolute z-10 top-1/2 -translate-y-1/2 text-base-content/40 ${sm ? 'left-2.5 text-base' : 'left-3'}`}
                    />
                    <input
                        type="text"
                        className={`input input-bordered w-full ${sm ? 'input-sm pl-9' : 'pl-10'}`}
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={e => {
                            setSearchTerm(e.target.value)
                            setShowDropdown(true)
                        }}
                        onFocus={() => {
                            setShowDropdown(true)
                            if (suppliers.length === 0) load()
                        }}
                        disabled={disabled}
                    />
                </div>
            )}

            {!selected &&
                renderPanel(
                    <>
                        {loading && (
                            <div className="p-3 text-center text-sm text-base-content/60">
                                <span className="loading loading-spinner loading-xs mr-2" />
                                Carregando...
                            </div>
                        )}
                        {!loading && filtered.length === 0 && (
                            <div className="p-3 text-center text-sm text-base-content/60">
                                Nenhum fornecedor encontrado
                            </div>
                        )}
                        {!loading &&
                            filtered.map((s: any) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                    onClick={() => {
                                        setSelected(s)
                                        onChange(s.id, s)
                                        setShowDropdown(false)
                                        setSearchTerm('')
                                    }}
                                >
                                    <span className="font-medium text-sm">
                                        {s.name}
                                    </span>
                                    {s.cnpj && (
                                        <span className="text-xs text-base-content/60 ml-2">
                                            CNPJ {s.cnpj}
                                        </span>
                                    )}
                                </button>
                            ))}
                    </>
                )}
        </div>
    )
}
